import { Component, ErrorInfo, ReactNode } from 'react';
import { Icon } from '../../components/common/Icon.tsx';

const NativeButton = 'button';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    tabName?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error(`[${this.props.tabName || 'Component'}] Error:`, error, errorInfo);
    }

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                    <Icon name="error" size="xl" className="mb-4 text-red-400" />
                    <h3 className="mb-2 font-bold text-gray-800 text-lg dark:text-gray-200">
                        Something went wrong
                    </h3>
                    <p className="mb-4 max-w-md text-gray-500 text-sm">
                        {this.props.tabName 
                            ? `The ${this.props.tabName} tab encountered an error.`
                            : 'An unexpected error occurred.'
                        }
                    </p>
                    <NativeButton
                        type="button"
                        onClick={() => this.setState({ hasError: false, error: null })}
                        className="rounded-lg bg-primary px-4 py-2 font-bold text-primary-content text-sm transition-all hover:brightness-110"
                    >
                        Try Again
                    </NativeButton>
                    {process.env.NODE_ENV === 'development' && this.state.error ? <pre className="mt-4 max-w-lg overflow-auto rounded bg-red-50 p-3 text-left text-red-600 text-xs dark:bg-red-900/20 dark:text-red-400">
                            {this.state.error.message}
                        </pre> : null}
                </div>
            );
        }

        return this.props.children;
    }
}














